import type { DiscountMapper } from '@domains/billing-settings/priv/CustomerDiscount/api/mappers'
import {
  type CreateDiscountRequest,
  type Discount,
  DiscountTypeDiscriminator,
  type FlatDiscount as FlatDiscountSpec,
} from '@mynt/backoffice-api-spec/discounts'
import { type CreateDiscount, DiscountModifier, type Discounts, DiscountType, type FlatDiscount } from '../../api/api'

type FlatDiscountDto = Discount & {
  discountType: FlatDiscountSpec
}

export class FlatDiscountDtoMapper implements DiscountMapper {
  discountTypeLabel = 'Flat discount'

  supports(discount: Discount): discount is FlatDiscountDto {
    return discount?.discountType?.type === DiscountTypeDiscriminator.FLAT_DISCOUNT
  }

  supportsType(discountType: string): boolean {
    return discountType === DiscountTypeDiscriminator.FLAT_DISCOUNT
  }

  supportsCreateDiscount(request: CreateDiscount): request is CreateDiscount<FlatDiscount> {
    return request.discountType.type === DiscountType.FlatDiscount
  }

  asDiscounts(discount: Discount): Discounts {
    if (!this.supports(discount)) {
      throw new Error('Unsupported discount type')
    }

    switch (discount.discountType.modifierType) {
      case DiscountModifier.ABSOLUTE: {
        return {
          modifier: DiscountModifier.ABSOLUTE,
          type: DiscountType.FlatDiscount,
          adornment: discount.discountType.absoluteAmount?.currency!,
          expirationDate: discount.expirationDate ? new Date(discount.expirationDate) : undefined,
          value: discount.discountType.absoluteAmount?.value!,
        }
      }

      case DiscountModifier.PERCENTAGE: {
        return {
          modifier: DiscountModifier.PERCENTAGE,
          type: DiscountType.FlatDiscount,
          adornment: '%',
          value: discount.discountType.percentageValue!,
          expirationDate: discount.expirationDate ? new Date(discount.expirationDate) : undefined,
        }
      }
    }

    throw new Error(`Unsupported discount modifier: ${discount.discountType.modifierType}`)
  }

  asCreateDiscountRequest(request: CreateDiscount): CreateDiscountRequest {
    if (!this.supportsCreateDiscount(request)) {
      throw new Error('Unsupported discount type')
    }

    switch (request.discountType.modifier) {
      case DiscountModifier.PERCENTAGE: {
        return {
          discountType: {
            type: DiscountTypeDiscriminator.FLAT_DISCOUNT,
            modifierType: request.discountType.modifier,
            percentageValue: request.discountType.percentageValue,
          },
          expirationDate: request.expirationDate,
        }
      }
      case DiscountModifier.ABSOLUTE: {
        return {
          discountType: {
            type: DiscountTypeDiscriminator.FLAT_DISCOUNT,
            modifierType: request.discountType.modifier,
            absoluteAmount: {
              value: request.discountType.absoluteAmount.value,
              currency: request.discountType.absoluteAmount.currency,
            },
          },
          expirationDate: request.expirationDate,
        }
      }
    }
  }
}
